import type React from 'react';
import { useRef, useState } from 'react';
import { LinkLogo } from '@/components/nav/link-logo';
import type { AppFormInstance } from '@/hooks/use-app-form';
import {
  Stepper,
  StepperItem,
  StepperIndicator,
  StepperTitle,
  StepperSeparator,
  StepperTrigger,
} from '@/components/ui/stepper';
import { useAppForm } from '@/hooks/use-app-form';
import { ProjectsService } from '@/api';
import { useAccountDetails } from '@/hooks/use-account-details';
import { authClient, setActiveOrganization } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  createFileRoute,
  Navigate,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ImpersonationBanner } from '@/components/super-admin/impersonation-banner';
import { DomainListEditor } from '@/components/domain-list-editor';
import type { DomainListEditorHandle } from '@/components/domain-list-editor';
import { extractRootDomain } from '@/utils/domain';
import { useFeatures } from '@/hooks/use-features';
import { hasPermission } from '@/lib/permissions';
import { getSafeAuthRedirectPath, isOAuthResumeURL } from '@/lib/auth-redirect';
import { toast } from 'sonner';
import { logspot } from '@logspot/web';
import { LeanOnboarding } from '@/components/onboarding/lean-onboarding';
import { pricingLinkSearch } from '@/lib/plans';

export const Route = createFileRoute('/_auth/onboarding')({
  component: Onboarding,
  // redirect: where to go after setup (e.g. the OAuth consent screen); plan + interval: the pricing-page fast lane.
  validateSearch: z.object({
    redirect: z.string().optional(),
    ...pricingLinkSearch,
  }),
  onEnter: () => {
    logspot.track({ event: 'Onboarding Step Viewed', metadata: { step: 'start' } });
  },
});

const orgSchema = z.object({
  orgName: z
    .string()
    .min(2, 'Organization name must be at least 2 characters.'),
  slug: z
    .string()
    .min(2, 'Slug must be at least 2 characters.')
    .regex(
      /^[a-z0-9-]+$/,
      'Slug can only contain lowercase letters, numbers, and hyphens.',
    ),
});

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-');
}

function Onboarding() {
  const { hasActiveOrg, userRole } = useRouteContext({ from: '/_auth' });
  const { redirect: returnTo, plan, interval, events } = Route.useSearch();
  const { isEnabled } = useFeatures();
  const canSetUp = !hasActiveOrg || hasPermission(userRole, { organization: ['update'] });
  if (isEnabled('leanOnboarding') && canSetUp) {
    return (
      <LeanOnboarding
        hasActiveOrg={hasActiveOrg}
        returnTo={returnTo}
        fastLane={plan ? { plan, interval: interval ?? 'monthly', events: events ?? 10_000 } : undefined}
      />
    );
  }
  return <ClassicOnboarding />;
}

function ClassicOnboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasActiveOrg } = useRouteContext({ from: '/_auth' });
  const { redirect: returnTo } = Route.useSearch();

  // Everyone starts on the no-card trial; the plan is chosen later from Billing.
  const steps: Array<'organization' | 'project'> = [
    ...(hasActiveOrg ? [] : (['organization'] as const)),
    'project',
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orgCreated, setOrgCreated] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [domainList, setDomainList] = useState<string[]>([]);
  const [domainError, setDomainError] = useState<string | null>(null);
  const domainEditorRef = useRef<DomainListEditorHandle>(null);

  const currentStepType = steps[activeStep];

  const orgForm = useAppForm({
    defaultValues: { orgName: '', slug: '' } as z.infer<typeof orgSchema>,
    validators: { onSubmit: orgSchema },
    onSubmit: async ({ value }) => {
      if (orgCreated) {
        // Update the org if the user went back and changed name/slug
        const { error } = await authClient.organization.update({
          data: { name: value.orgName, slug: value.slug },
        });
        if (error) {
          const isSlugConflict =
            error.status === 409 || error.message?.toLowerCase().includes('slug');
          toast.error(
            isSlugConflict ? 'Slug already taken' : 'Failed to update organization',
            {
              description: isSlugConflict
                ? 'Another organization is using that slug. Choose a different one.'
                : (error.message || 'Please try again.'),
            },
          );
          return;
        }
        setActiveStep((s) => s + 1);
        return;
      }

      const { data, error } = await authClient.organization.create({
        name: value.orgName,
        slug: value.slug,
      });

      if (error) {
        toast.error('Failed to create organization', {
          description: error.message || 'Please try again.',
        });
        return;
      }

      if (data) {
        await setActiveOrganization(data.id, queryClient);
      }

      setOrgCreated(true);
      setActiveStep((s) => s + 1);
    },
  });

  // Created last so a tab closed mid-wizard leaves no half-set-up project.
  const handleProjectSubmit = async () => {
    // Flush a typed-but-not-added domain so the user doesn't have to click
    // "Add" before submitting; null means the pending input was invalid, so
    // abort (the inline error is already shown).
    const committed = domainEditorRef.current?.commitPending();
    if (committed === null) return;
    const finalDomains = committed ?? domainList;
    const [primaryDomain] = finalDomains;
    if (!primaryDomain) {
      setDomainError('Add at least one domain to continue.');
      return;
    }
    setDomainError(null);
    setIsSubmitting(true);
    try {
      const newProject = await ProjectsService.createProject({
        name: extractRootDomain(primaryDomain),
        domainWhitelist: finalDomains,
        silent: true,
      });
      if (!newProject) {
        setIsSubmitting(false);
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['account-details'] });

      logspot.track({
        event: 'Onboarding Completed',
        metadata: { flow: 'classic', plan: 'free' },
      });

      if (returnTo) {
        // Refresh the cached session and org list first: the consent screen
        // checks them and would otherwise send the user back here.
        await queryClient.invalidateQueries({ queryKey: ['auth'] });
        if (isOAuthResumeURL(returnTo)) window.location.assign(returnTo);
        else navigate({ to: getSafeAuthRedirectPath(returnTo) });
        return;
      }
      navigate({ to: '/start-tracking' });
    } catch (err) {
      toast.error('Something went wrong', {
        description: err instanceof Error ? err.message : 'Please try again.',
      });
      setIsSubmitting(false);
    }
  };

  const stepLabels: Record<(typeof steps)[number], string> = {
    organization: 'Organization',
    project: 'Project',
  };

  const canGoBack = activeStep > 0;
  const isOrgStep = currentStepType === 'organization';
  const isProjectStep = currentStepType === 'project';

  // Wrap in guards when user already has an org — redirects away if they
  // already completed onboarding, otherwise renders the wizard.
  const Wrapper = hasActiveOrg && !orgCreated ? OnboardingGuards : PassThrough;

  return (
    <Wrapper suspendRedirect={isSubmitting} returnTo={returnTo}>
      <div className="flex h-dvh flex-col">
        <ImpersonationBanner />
        {/* Top bar: title + action buttons */}
        <div className="flex min-h-16 shrink-0 items-center justify-between border-b px-6">
          <div className="flex items-center gap-3">
            <LinkLogo />
            <div className="bg-primary h-6 w-px" />
            <h1 className="text-foreground text-lg font-semibold">
              Get Started
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {canGoBack && (
              <Button
                variant="outline"
                onClick={() => setActiveStep((s) => s - 1)}
              >
                Previous
              </Button>
            )}
            {isOrgStep && (
              <Button
                loading={orgForm.state.isSubmitting}
                onClick={() => orgForm.handleSubmit()}
              >
                Next
              </Button>
            )}
            {isProjectStep && (
              <Button loading={isSubmitting} onClick={handleProjectSubmit}>
                Start Tracking
              </Button>
            )}
          </div>
        </div>

        {/* Stepper bar */}
        <div className="shrink-0 border-b px-6 py-3">
          <Stepper
            value={activeStep}
            onValueChange={setActiveStep}
            className="w-full min-w-max sm:min-w-0"
          >
            {steps.map((step, i) => (
              <StepperItem
                key={step}
                step={i}
                className={i < steps.length - 1 ? 'flex-1' : undefined}
              >
                <StepperTrigger className="w-full justify-start">
                  <StepperIndicator />
                  <StepperTitle className="hidden sm:block">
                    {stepLabels[step]}
                  </StepperTitle>
                </StepperTrigger>
                {i < steps.length - 1 && <StepperSeparator />}
              </StepperItem>
            ))}
          </Stepper>
        </div>

        {/* Step content */}
        <div className="min-h-0 grow overflow-auto">
          <div className="px-6 py-10">
            {currentStepType === 'organization' && (
              <form
                className="space-y-8"
                onSubmit={(e) => {
                  e.preventDefault();
                  orgForm.handleSubmit();
                }}
              >
                <OrgFormFields
                  orgForm={orgForm}
                  slugTouched={slugTouched}
                  setSlugTouched={setSlugTouched}
                  disabled={false}
                />
              </form>
            )}

            {currentStepType === 'project' && (
              <form
                className="space-y-8"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleProjectSubmit();
                }}
              >
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Domain Whitelist <span className="text-primary">*</span>
                  </label>
                  <p className="text-muted-foreground text-sm">
                    Enter the domain you want to track. We'll create your first
                    project for it. Not sure yet? You can change or add domains
                    anytime in Settings.
                  </p>
                  <DomainListEditor
                    ref={domainEditorRef}
                    domains={domainList}
                    onChange={(next) => {
                      setDomainList(next);
                      if (domainError) setDomainError(null);
                    }}
                    allowEmpty
                  />
                  {domainError && (
                    <p className="text-destructive text-sm">{domainError}</p>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers and sub-components
// ---------------------------------------------------------------------------

type WrapperProps = {
  children: React.ReactNode;
  /**
   * When true, OnboardingGuards stops redirecting away from the wizard. Used
   * during the final-step submission so the freshly-invalidated
   * account-details cache can't bounce the user to "/" before our own
   * navigate (free) or window.location.href (paid → Stripe) lands. Without
   * this, the user briefly sees the dashboard before Stripe takes over.
   */
  suspendRedirect?: boolean;
  // Where an already-onboarded user goes instead of the dashboard.
  returnTo?: string;
};

function PassThrough({ children }: WrapperProps) {
  return <>{children}</>;
}

function OnboardingGuards({ children, suspendRedirect, returnTo }: WrapperProps) {
  const { hasCreatedAnyProject } = useAccountDetails();

  if (suspendRedirect) {
    return <>{children}</>;
  }

  if (hasCreatedAnyProject) {
    if (isOAuthResumeURL(returnTo)) return <Navigate to="/" href={returnTo} reloadDocument />;
    return <Navigate to={returnTo ? getSafeAuthRedirectPath(returnTo) : '/'} />;
  }

  // User has an org but no project — render the wizard (starting at plan step)
  return <>{children}</>;
}

function getErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return null;
}

type OrgFormData = z.infer<typeof orgSchema>;

function OrgFormFields({
  orgForm,
  slugTouched,
  setSlugTouched,
  disabled,
}: {
  orgForm: AppFormInstance<OrgFormData>;
  slugTouched: boolean;
  setSlugTouched: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-8">
      <orgForm.AppField name="orgName">
        {(field) => {
          const isInvalid =
            field.state.meta.isTouched && !field.state.meta.isValid;
          const errorMsg = isInvalid
            ? getErrorMessage(field.state.meta.errors?.[0])
            : null;
          return (
            <div className="flex flex-col gap-2">
              <label htmlFor={field.name} className="text-sm font-medium">
                Organization Name{' '}
                <span
                  className={isInvalid ? 'text-destructive' : 'text-primary'}
                >
                  *
                </span>
              </label>
              <p className="text-muted-foreground text-sm">
                The name of your company, team, or personal workspace.
              </p>
              <Input
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                  if (!slugTouched) {
                    orgForm.setFieldValue('slug', slugify(e.target.value));
                  }
                }}
                placeholder="Your Company or Team Name..."
                className="h-10"
                aria-invalid={isInvalid}
                disabled={disabled}
              />
              {errorMsg && (
                <p className="text-destructive text-sm">{errorMsg}</p>
              )}
            </div>
          );
        }}
      </orgForm.AppField>
      <orgForm.AppField name="slug">
        {(field) => {
          const isInvalid =
            field.state.meta.isTouched && !field.state.meta.isValid;
          const errorMsg = isInvalid
            ? getErrorMessage(field.state.meta.errors?.[0])
            : null;
          return (
            <div className="flex flex-col gap-2">
              <label htmlFor={field.name} className="text-sm font-medium">
                Slug{' '}
                <span
                  className={isInvalid ? 'text-destructive' : 'text-primary'}
                >
                  *
                </span>
              </label>
              <p className="text-muted-foreground text-sm">
                A URL-friendly identifier. Lowercase letters, numbers, and
                hyphens only.
              </p>
              <Input
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                  setSlugTouched(true);
                }}
                placeholder="your-organization"
                className="h-10"
                aria-invalid={isInvalid}
                disabled={disabled}
              />
              {errorMsg && (
                <p className="text-destructive text-sm">{errorMsg}</p>
              )}
            </div>
          );
        }}
      </orgForm.AppField>
    </div>
  );
}
